import "./JobDetailForm.css";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Form } from "react-bootstrap";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import MatchBiasSlider from "../MatchBiasSlider/MatchBiasSlider";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

const JobDetailForm = ({
    jobId = null,
    job_object = null,
    closeModal = null,
    triggerReload = null,
}) => {
    const [job, setJob] = useState({
        title: "",
        client: "",
        contact: "",
        industry: "",
        key_requirements: "",
        description: "",
        requirements: "",
        desirables: "",
        skills: "",
        min_years: 0,
        max_years: 0,
        match_bias: 50,
        locality: "",
        administrative_area: "",
        country: "",
        postal_code: "",
        external_id: "",
        secondary_external_id: "",
        ats_company_connection_id: null,
    });
    const [cookies] = useCookies(["AuthorizationToken"]);
    const [isEditing, setIsEditing] = useState(jobId != null || job_object != null);
    const [matchSliderValue, setMatchSliderValue] = useState(50);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingATSJob, setIsLoadingATSJob] = useState(false);
    const [isLoadingATSAccess, setIsLoadingATSAccess] = useState(false);
    const [isJobImported, setIsJobImported] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const [showATSTab, setShowATSTab] = useState(false);
    const [atsJobs, setAtsJobs] = useState([]);
    const [selectedATSJob, setSelectedATSJob] = useState(null);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchJobDetails = async () => {
            try {
                const response = await axios.get(
                    `https://api.foveaai.com/evaluate/web/jobs/${jobId}`,
                    {
                        headers: { Authorization: cookies.AuthorizationToken },
                    }
                );
                const jobData =
                    typeof response.data === "string"
                        ? JSON.parse(response.data)
                        : response.data;
                setJob(jobData);

                // Set the match slider value to the job's match_bias
                // if the job object is fetched successfully and has a match_bias
                // that is not null or "".
                if (
                    jobData.match_bias !== null &&
                    jobData.match_bias !== undefined
                ) {
                    setMatchSliderValue(jobData.match_bias);
                }
            } catch (error) {
                console.error("Error fetching job details:", error);
            }
        };

        // Fetch the job details if the job object is not provided
        if (jobId && job_object == null) {
            fetchJobDetails();
            setIsEditing(true);
        }

        // Otherwise, use the job object
        else if (job_object != null) {
            setJob(job_object);
            setIsEditing(true);
        }

        // We are creating a new job object
        else {
            setIsEditing(false);
        }
    }, [jobId, job_object, cookies.AuthorizationToken]);

    useEffect(() => {
        const checkATSJobs = async () => {
            setIsLoadingATSAccess(true);
            try {
                const response = await axios.get(
                    "https://api.foveaai.com/evaluate/web/ats/jobs",
                    {
                        headers: { Authorization: cookies.AuthorizationToken },
                        params: { status: "CLOSED" }
                    }
                );

                if (response.status === 200) {
                    const data = typeof response.data === "string"
                        ? JSON.parse(response.data)
                        : response.data;
                    setShowATSTab(Array.isArray(data) && data.length > 0);
                    setAtsJobs(Array.isArray(data) ? data : []);
                }
            } catch (error) {
                console.error("Error checking ATS jobs:", error);
                setShowATSTab(false);
            } finally {
                setIsLoadingATSAccess(false);
            }
        };

        if (cookies.AuthorizationToken && !isEditing) {
            checkATSJobs();
        } else if (isEditing) {
            // When in editing mode, don't show ATS tab and don't make API call
            setShowATSTab(false);
            setAtsJobs([]);
        }
    }, [cookies.AuthorizationToken, isEditing]);

    const handleMatchSliderChange = (value) => {
        // Set the match slider value and update the job state
        setMatchSliderValue(value);
        setJob({ ...job, match_bias: value });
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortedAtsJobs = [...atsJobs].sort((a, b) => {
        if (!sortField) return 0;
        
        let aValue = a[sortField];
        let bValue = b[sortField];
        
        // Handle date sorting
        if (sortField === 'remote_created_at') {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
        }
        
        // Handle string sorting
        if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const handleJobSelect = (job) => {
        // Store the complete job object including external_id and ats_connection_id for future API calls
        // These IDs will be used to call the individual job GET API endpoint to populate form fields
        const isCurrentlySelected = selectedATSJob?.id === job.id;
        const newSelection = isCurrentlySelected ? null : {
            ...job,
            external_id: job.id, // Map ATS 'id' to 'external_id' for internal use
            secondary_external_id: job.secondary_id, // Map ATS 'secondary_id' to 'secondary_external_id'
            ats_connection_id: job.ats_connection_id
        };
        
        setSelectedATSJob(newSelection);
        
        // Reset import status when a new job is selected
        if (newSelection) {
            setIsJobImported(false);
            console.log('Selected ATS Job:', {
                title: newSelection.title,
                external_id: newSelection.external_id,
                secondary_external_id: newSelection.secondary_external_id,
                ats_connection_id: newSelection.ats_connection_id
            });
        }
    };

    const fetchATSJobDetails = async (atsJob) => {
        setIsLoadingATSJob(true); // Show loading overlay
        try {
            const response = await axios.get(
                `https://api.foveaai.com/evaluate/web/ats/job/${atsJob.id}/connection/${atsJob.ats_connection_id}`,
                {
                    headers: { Authorization: cookies.AuthorizationToken },
                }
            );
            
            const detailedJob = typeof response.data === "string"
                ? JSON.parse(response.data)
                : response.data;
                
            // Populate the form with ATS job data, mapping to internal field names
            setJob({
                ...job, // Keep existing form values
                title: detailedJob.title || "",
                client: detailedJob.client || "",
                contact: detailedJob.contact || "",
                industry: detailedJob.industry || "",
                key_requirements: detailedJob.key_requirements || "",
                description: detailedJob.description || "",
                requirements: detailedJob.requirements || "",
                desirables: detailedJob.desirables || "",
                skills: detailedJob.skills || "",
                min_years: detailedJob.minimum_years || detailedJob.min_years || 0,
                max_years: detailedJob.maximum_years || detailedJob.max_years || 0,
                match_bias: detailedJob.match_bias ? (detailedJob.match_bias * 100) : 50, // Convert from 0-1 to 0-100
                locality: detailedJob.city || "",
                administrative_area: detailedJob.state || "",
                country: detailedJob.country || "",
                postal_code: detailedJob.zip_code || "",
                external_id: atsJob.external_id,
                secondary_external_id: atsJob.secondary_external_id,
                ats_company_connection_id: atsJob.ats_connection_id,
            });
            
            // Update match slider value
            if (detailedJob.match_bias) {
                setMatchSliderValue(detailedJob.match_bias * 100);
            }
            
            // Mark job as successfully imported and switch to Basics tab
            setIsJobImported(true);
            setSelectedTab(showATSTab ? 1 : 0); // Switch to Basics tab (accounting for ATS tab)
            
        } catch (error) {
            console.error("Error fetching ATS job details:", error);
        } finally {
            setIsLoadingATSJob(false); // Hide loading overlay
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'N/A';
        }
    };

    // Log the job object whenever it changes for debugging
    useEffect(() => {
        console.log("Job object:", job);
    }, [job]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Check if the field being changed is 'max_years' or 'min_years'
        if (name === "max_years" || name === "min_years") {
            const floatValue = parseFloat(value);
            // Upate the state with the converted float value or 0 if not a number
            setJob({ ...job, [name]: isNaN(floatValue) ? 0 : floatValue });
        } else {
            // Update the state with the new value
            setJob({ ...job, [name]: value });
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true); // Activate loading overlay

        // Editing an existing job, so use the PUT method
        if (isEditing) {
            try {
                console.log("JobDetailForm put job:", job);
                const response = await axios.put(
                    `https://api.foveaai.com/evaluate/web/jobs/${jobId}`,
                    job,
                    { headers: { Authorization: cookies.AuthorizationToken } }
                );

                // Get the updated job id from the response
                const job_response =
                    typeof response.data === "string"
                        ? JSON.parse(response.data)
                        : response.data;
                setJob(job_response);

                // Redirect to the job page using the updated job's ID with the React Router
                if (triggerReload) triggerReload();
                if (closeModal) closeModal();
            } catch (error) {
                console.error("Error updating job details:", error);
            } finally {
                setIsSubmitting(false); // Deactivate loading overlay
            }

            // Adding a new job, so use the POST method
        } else {
            try {
                console.log("JobDetailForm post job:", job);
                const response = await axios.post(
                    "https://api.foveaai.com/evaluate/web/jobs",
                    job,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: cookies.AuthorizationToken,
                        },
                    }
                );

                // Get the new job id from the response
                const job_response =
                    typeof response.data === "string"
                        ? JSON.parse(response.data)
                        : response.data;
                setJob(job_response);

                // Redirect to the job page using the new job's ID with the React Router
                if (closeModal) closeModal();
                navigate(`/job/${job_response.job_id}`);
            } catch (error) {
                console.error("Error updating job details:", error);
            } finally {
                setIsSubmitting(false); // Deactivate loading overlay
            }
        }
    };

    // Derived UI state for checklist completion
    const checklist = {
        basics: Boolean((job.title || "").trim()) && Boolean((job.industry || "").trim()),
        location:
            (
                Boolean((job.locality || "").trim()) &&
                Boolean((job.administrative_area || "").trim()) &&
                Boolean((job.country || "").trim())
            ) || Boolean((job.postal_code || "").trim()),
        requirements:
            Boolean((job.description || "").trim()) &&
            Boolean((job.requirements || "").trim()) &&
            Boolean((job.key_requirements || "").trim()),
        experience:
            (job.min_years ?? 0) > 0 && (job.max_years ?? 0) > (job.min_years ?? 0),
    };

    const totalTabs = showATSTab ? 5 : 4;
    const progressPct = ((selectedTab + 1) / totalTabs) * 100;

    return (
        <div className="job-detail-container">
            {(isSubmitting || isLoadingATSJob || isLoadingATSAccess) && <LoadingOverlay />}
            <div className="job-modal">
                <div className="job-modal-header">
                    <div className="header-left">
                        <div className="job-modal-title">
                            {isEditing ? "Edit Job" : "Add New Job Opening"}
                        </div>
                    </div>
                    {closeModal && (
                        <button type="button" className="close-btn" onClick={closeModal} aria-label="Close">
                            ✕
                        </button>
                    )}
                </div>
                <div className="job-modal-progress">
                    <div className="progress-fill" style={{ width: `${progressPct}%` }} />
                </div>

                <Form onSubmit={handleSubmit}>
                    <div className="job-modal-body">
                        <div className="job-main-content">
                            <Tabs
                                selectedIndex={selectedTab}
                                onSelect={(index) => setSelectedTab(index)}
                                forceRenderTabPanel={true}
                            >
                                <TabList className="job-tabs">
                                    {showATSTab && <Tab className="job-tab">ATS</Tab>}
                                    <Tab className="job-tab">Basics</Tab>
                                    <Tab className="job-tab">Location</Tab>
                                    <Tab className="job-tab">Requirements</Tab>
                                    <Tab className="job-tab">Fine Tune</Tab>
                                </TabList>

                                {showATSTab && (
                                    <TabPanel>
                                        <div className="tab-content">
                                            <div className="quick-tips">
                                                <div className="tips-icon">🔗</div>
                                                <div className="tips-content">
                                                    <div className="tips-title">Add a job from your ATS</div>
                                                    <div className="tips-text">
                                                        Carefully review the job details in the other tabs before adding this job. New and existing candidates will be automatically evaluated as they are added to your ATS.
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="form-group">
                                                <div className="ats-jobs-table">
                                                    <table className="modern-table">
                                                        <thead>
                                                            <tr>
                                                                <th style={{width: '60px'}}></th>
                                                                <th 
                                                                    style={{cursor: 'pointer'}}
                                                                    onClick={() => handleSort('title')}
                                                                    className={sortField === 'title' ? 'sortable sorted' : 'sortable'}
                                                                >
                                                                    Title 
                                                                    {sortField === 'title' && (
                                                                        <span className="sort-indicator">
                                                                            {sortDirection === 'asc' ? '↑' : '↓'}
                                                                        </span>
                                                                    )}
                                                                </th>
                                                                <th 
                                                                    style={{cursor: 'pointer', width: '140px'}}
                                                                    onClick={() => handleSort('remote_created_at')}
                                                                    className={sortField === 'remote_created_at' ? 'sortable sorted' : 'sortable'}
                                                                >
                                                                    Created 
                                                                    {sortField === 'remote_created_at' && (
                                                                        <span className="sort-indicator">
                                                                            {sortDirection === 'asc' ? '↑' : '↓'}
                                                                        </span>
                                                                    )}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {sortedAtsJobs.length === 0 ? (
                                                                <tr>
                                                                    <td colSpan="3" className="no-jobs-message">
                                                                        No open jobs found in your ATS connections.
                                                                    </td>
                                                                </tr>
                                                            ) : (
                                                                sortedAtsJobs.map((job, index) => (
                                                                    <tr 
                                                                        key={job.id || index}
                                                                        className={`table-row ${selectedATSJob?.id === job.id ? 'selected' : ''}`}
                                                                        onClick={() => handleJobSelect(job)}
                                                                    >
                                                                        <td>
                                                                            {job.ats_square_logo_url ? (
                                                                                <img 
                                                                                    src={job.ats_square_logo_url} 
                                                                                    alt={job.ats_name}
                                                                                    className="ats-logo"
                                                                                />
                                                                            ) : (
                                                                                <div className="ats-logo-placeholder">
                                                                                    {job.ats_name?.charAt(0) || 'A'}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td className="job-title">{job.title || 'Untitled'}</td>
                                                                        <td>{formatDate(job.remote_created_at)}</td>
                                                                    </tr>
                                                                ))
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                
{!isJobImported && (
                                                    <div className="import-job-button-container" style={{ marginTop: '4px', textAlign: 'right' }}>
                                                        <Button
                                                            variant="primary"
                                                            disabled={!selectedATSJob}
                                                            onClick={() => fetchATSJobDetails(selectedATSJob)}
                                                            className="btn-modern-primary"
                                                        >
                                                            Import Job
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </TabPanel>
                                )}

                                <TabPanel>
                                    <div className="tab-content">
                                        <div className="quick-tips">
                                            <div className="tips-icon">💡</div>
                                            <div className="tips-content">
                                                <div className="tips-title">Consider job titles</div>
                                                <div className="tips-text">
                                                    An accurate job title and industry help improve candidate matching.
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <Form.Label htmlFor="title" className="modern-label">
                                                Title
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="title"
                                                id="title"
                                                value={job.title || ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                                placeholder="e.g., Senior Frontend Engineer"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="client" className="modern-label">
                                                Client
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="client"
                                                id="client"
                                                value={job.client || ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                                placeholder="Company or organization"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="contact" className="modern-label">
                                                Contact
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="contact"
                                                id="contact"
                                                value={job.contact || ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                                placeholder="Hiring manager or recruiter"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="industry" className="modern-label">
                                                Industry
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="industry"
                                                id="industry"
                                                value={job.industry || ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                                placeholder="e.g., Aerospace, Software, Finance"
                                            />
                                        </div>
                                    </div>
                                </TabPanel>

                                <TabPanel>
                                    <div className="tab-content">
                                        <div className="quick-tips">
                                            <div className="tips-icon">📍</div>
                                            <div className="tips-content">
                                                <div className="tips-title">Real-world commute</div>
                                                <div className="tips-text">
                                                    The job location is used to calculate approximate commute time and distance. (Optional)
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <Form.Label htmlFor="locality" className="modern-label">
                                                City
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="locality"
                                                id="locality"
                                                value={job.locality || ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                                placeholder="e.g., Seattle"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="administrative_area" className="modern-label">
                                                State/Province
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="administrative_area"
                                                id="administrative_area"
                                                value={job.administrative_area || ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                                placeholder="e.g., WA"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="country" className="modern-label">
                                                Country
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="country"
                                                id="country"
                                                value={job.country || ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                                placeholder="e.g., United States"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="postal_code" className="modern-label">
                                                Postal Code
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="postal_code"
                                                id="postal_code"
                                                value={job.postal_code || ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                                placeholder="e.g., 98101"
                                            />
                                        </div>
                                    </div>
                                </TabPanel>

                                <TabPanel>
                                    <div className="tab-content">
                                        <div className="quick-tips">
                                            <div className="tips-icon">📝</div>
                                            <div className="tips-content">
                                                <div className="tips-title">Improving match accuracy</div>
                                                <div className="tips-text">
                                                    Describe the skills and experience that are important for the role. You can write using bullet points, descriptions, or a mix of both.
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-group key-req-group">
                                            <Form.Label htmlFor="key_requirements" className="modern-label">
                                                Key Requirements
                                            </Form.Label>
                                            <div className="field-help">Most important for matching — include only the non‑negotiable, minimum requirements.</div>
                                            <Form.Control
                                                as="textarea"
                                                rows={5}
                                                name="key_requirements"
                                                id="key_requirements"
                                                value={job.key_requirements || ""}
                                                onChange={handleInputChange}
                                                className="modern-textarea"
                                                placeholder="Bullet or short sentences of mandatory qualifications"
                                            />
                                            <div className="character-count">{(job.key_requirements || "").length} chars</div>
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="description" className="modern-label">
                                                Description
                                            </Form.Label>
                                            <div className="field-help">A broad description of the role, including key responsibilities and expectations.</div>
                                            <Form.Control
                                                as="textarea"
                                                rows={5}
                                                name="description"
                                                id="description"
                                                value={job.description || ""}
                                                onChange={handleInputChange}
                                                className="modern-textarea"
                                                placeholder="Role overview, team, and scope"
                                            />
                                            <div className="character-count">{(job.description || "").length} chars</div>
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="requirements" className="modern-label">
                                                Requirements
                                            </Form.Label>
                                            <div className="field-help">Not quite key-requirements, but still important to the role. (Optional)</div>
                                            <Form.Control
                                                as="textarea"
                                                rows={5}
                                                name="requirements"
                                                id="requirements"
                                                value={job.requirements || ""}
                                                onChange={handleInputChange}
                                                className="modern-textarea"
                                                placeholder="Required experience, education, and skills"
                                            />
                                            <div className="character-count">{(job.requirements || "").length} chars</div>
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="desirables" className="modern-label">
                                                Desirables
                                            </Form.Label>
                                            <div className="field-help">Nice to have qualities and skills, but not essential. (Optional)</div>
                                            <Form.Control
                                                as="textarea"
                                                rows={5}
                                                name="desirables"
                                                id="desirables"
                                                value={job.desirables || ""}
                                                onChange={handleInputChange}
                                                className="modern-textarea"
                                                placeholder="Nice-to-haves"
                                            />
                                            <div className="character-count">{(job.desirables || "").length} chars</div>
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="skills" className="modern-label">
                                                Skills
                                            </Form.Label>
                                            <div className="field-help">Any skills not already included in the requirements. (Optional)</div>
                                            <Form.Control
                                                type="text"
                                                name="skills"
                                                id="skills"
                                                value={job.skills || ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                                placeholder="Comma-separated skills"
                                            />
                                        </div>
                                    </div>
                                </TabPanel>

                                <TabPanel>
                                    <div className="tab-content">
                                        <div className="quick-tips">
                                            <div className="tips-icon">⚙️</div>
                                            <div className="tips-content">
                                                <div className="tips-title">Tune match scoring</div>
                                                <div className="tips-text">
                                                    Set preferred experience and adjust flexibility to widen or narrow matching. These settings can be adjusted after evaluation to refine results.
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <Form.Label htmlFor="min_years" className="modern-label">
                                                Minimum Years of Experience
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="min_years"
                                                id="min_years"
                                                value={job.min_years}
                                                onChange={handleInputChange}
                                                min={1}
                                                className="modern-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="max_years" className="modern-label">
                                                Maximum Years of Experience
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="max_years"
                                                id="max_years"
                                                value={job.max_years}
                                                onChange={handleInputChange}
                                                min={1}
                                                className="modern-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="match_bias" className="modern-label">
                                                Experience Flexibility
                                            </Form.Label>
                                            <div className="slider-group">
                                                <MatchBiasSlider
                                                    matchSliderValue={matchSliderValue}
                                                    onMatchSliderChange={handleMatchSliderChange}
                                                    disabled={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </TabPanel>
                            </Tabs>
                        </div>

                        <aside className="job-side-panel">
                            <div className="side-panel-title">Checklist</div>
                            <div className="checklist">
                                <div className={`checklist-item ${checklist.basics ? "completed" : ""}`}>
                                    <div className="checklist-checkbox" />
                                    Basics
                                </div>
                                <div className={`checklist-item ${checklist.location ? "completed" : ""}`}>
                                    <div className="checklist-checkbox" />
                                    Location
                                </div>
                                <div className={`checklist-item ${checklist.requirements ? "completed" : ""}`}>
                                    <div className="checklist-checkbox" />
                                    Requirements
                                </div>
                                <div className={`checklist-item ${checklist.experience ? "completed" : ""}`}>
                                    <div className="checklist-checkbox" />
                                    Fine Tune
                                </div>
                            </div>
                            <a
                                href="https://foveaai.com/ai-recruiting-resources-and-guides-foveaai/"
                                target="_blank"
                                rel="noreferrer"
                                className="best-practices-link"
                            >
                                Tutorials
                            </a>
                        </aside>
                    </div>

                    <div className="job-modal-footer">
                        {closeModal && (
                            <Button
                                variant="secondary"
                                onClick={closeModal}
                                className="btn-modern-secondary"
                                type="button"
                            >
                                Cancel
                            </Button>
                        )}
                        <Button
                            variant="success"
                            type="submit"
                            className="btn-modern-primary"
                            disabled={showATSTab && selectedTab === 0 && !isEditing}
                        >
                            {isEditing ? "Save All Changes" : "Add Job"}
                        </Button>
                    </div>
                </Form>
            </div>
        </div>
    );
};

export default JobDetailForm;
